import { useEffect, useState } from "react";
import { api, inr, dateFmt, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, Check, Link2, Copy, Ban } from "lucide-react";
import { StatusBadge } from "./Dashboard";

const GST_RATES = [0, 5, 12, 18, 28];

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // form
  const todayIso = new Date().toISOString().slice(0, 10);
  const dueIso = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    client_id: "",
    issue_date: todayIso,
    due_date: dueIso,
    items: [{ description: "", quantity: 1, rate: 0 }],
    gst_rate: 18,
    is_inter_state: false,
    notes: "",
  });

  const fetchAll = async () => {
    const [inv, cl] = await Promise.all([api.get("/invoices"), api.get("/clients")]);
    setInvoices(inv.data || []);
    setClients(cl.data || []);
  };

  useEffect(() => { fetchAll(); }, []);

  const subtotal = form.items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.rate || 0), 0);
  const gstAmt = subtotal * (Number(form.gst_rate || 0) / 100);
  const total = subtotal + gstAmt;

  const submit = async () => {
    if (!form.client_id) { toast.error("Pick a client"); return; }
    if (form.items.some((i) => !i.description || !i.rate)) { toast.error("Fill all line items"); return; }
    setLoading(true);
    try {
      await api.post("/invoices", {
        ...form,
        items: form.items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity || 1),
          rate: Number(i.rate || 0),
        })),
        gst_rate: Number(form.gst_rate),
      });
      toast.success("Invoice created");
      setOpen(false);
      setForm({ ...form, items: [{ description: "", quantity: 1, rate: 0 }], notes: "" });
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };

  const markPaid = async (id) => {
    await api.patch(`/invoices/${id}`, { status: "paid" });
    toast.success("Marked paid");
    fetchAll();
  };
  const cancelInvoice = async (id) => {
    if (!window.confirm("Cancel this invoice? It stays in your records but is marked cancelled.")) return;
    await api.patch(`/invoices/${id}`, { status: "cancelled" });
    toast.success("Invoice cancelled");
    fetchAll();
  };
  const createPaymentLink = async (i) => {
    try {
      const r = await api.post(`/invoices/${i.id}/payment-link`);
      const url = r.data?.short_url;
      if (url) {
        try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
        toast.success(r.data?.existing ? "Payment link copied" : "Payment link created & copied");
      }
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't create link");
    }
  };
  const copyPaymentLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const sendByEmail = async (i) => {
    try {
      // Fetch prefilled email payload
      const r = await api.get(`/invoices/${i.id}/email-payload`);
      const { to, subject, body } = r.data;
      // Trigger PDF download so the user can attach manually
      await downloadPdf(i);
      // Open user's email client with prefilled fields
      const mailto = `mailto:${encodeURIComponent(to || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
      // Mark as sent
      await api.patch(`/invoices/${i.id}`, { status: "sent" });
      toast.success("PDF downloaded · email opened · marked as sent");
      fetchAll();
    } catch (e) {
      toast.error("Couldn't open email");
    }
  };
  const remove = async (id) => {
    await api.delete(`/invoices/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  const downloadPdf = async (i) => {
    const r = await api.get(`/invoices/${i.id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `${i.invoice_number}.pdf`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const updateItem = (idx, field, value) => {
    setForm((f) => {
      const items = f.items.slice();
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, items };
    });
  };

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="tc-overline mb-2">Money on the way</p>
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">Invoices</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="invoice-new-btn"
              disabled={clients.length === 0}
              className="bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md"
            >
              <Plus size={16} className="mr-1" /> New invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] text-2xl">Create invoice</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Client</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="invoice-client-select" className="mt-2 h-11 rounded-md border-[#e5e5e0]">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">GST rate</Label>
                  <Select value={String(form.gst_rate)} onValueChange={(v) => setForm({ ...form, gst_rate: Number(v) })}>
                    <SelectTrigger data-testid="invoice-gst-select" className="mt-2 h-11 rounded-md border-[#e5e5e0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GST_RATES.map((g) => (<SelectItem key={g} value={String(g)}>{g}%</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Issue date</Label>
                  <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                    className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid="invoice-issue-date" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Due date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid="invoice-due-date" />
                </div>
              </div>

              <div className="flex items-center justify-between bg-[#f4f1eb] rounded-md px-4 py-3">
                <div>
                  <div className="text-sm font-medium">Inter-state (IGST)</div>
                  <div className="text-xs text-[#5a5a53]">Off = CGST + SGST split</div>
                </div>
                <Switch
                  checked={form.is_inter_state}
                  onCheckedChange={(v) => setForm({ ...form, is_inter_state: v })}
                  data-testid="invoice-igst-switch"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Line items</Label>
                  <Button variant="ghost" size="sm" data-testid="invoice-add-item"
                    onClick={() => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, rate: 0 }] })}>
                    <Plus size={14} className="mr-1" /> Add item
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2">
                      <Input placeholder="Description" value={it.description}
                        onChange={(e) => updateItem(idx, "description", e.target.value)}
                        className="col-span-7 h-10 rounded-md border-[#e5e5e0]"
                        data-testid={`invoice-item-desc-${idx}`} />
                      <Input type="number" placeholder="Qty" value={it.quantity}
                        onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                        className="col-span-2 h-10 rounded-md border-[#e5e5e0]"
                        data-testid={`invoice-item-qty-${idx}`} />
                      <Input type="number" placeholder="Rate" value={it.rate}
                        onChange={(e) => updateItem(idx, "rate", e.target.value)}
                        className="col-span-2 h-10 rounded-md border-[#e5e5e0]"
                        data-testid={`invoice-item-rate-${idx}`} />
                      <button type="button" className="col-span-1 text-[#5a5a53] hover:text-[#b83a26]"
                        onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-2 rounded-md border-[#e5e5e0]" rows={2} placeholder="Thank you note, payment terms..." />
              </div>

              <div className="border border-[#e5e5e0] rounded-md p-4 bg-[#fafaf8]">
                <div className="flex justify-between text-sm py-1"><span className="text-[#5a5a53]">Subtotal</span><span className="font-mono">{inr(subtotal)}</span></div>
                <div className="flex justify-between text-sm py-1"><span className="text-[#5a5a53]">GST ({form.gst_rate}%)</span><span className="font-mono">{inr(gstAmt)}</span></div>
                <div className="flex justify-between text-base py-2 border-t border-[#e5e5e0] mt-1"><span className="font-bold">Total</span><span className="font-bold font-mono text-[#d95d39]">{inr(total)}</span></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="invoice-submit" onClick={submit} disabled={loading}
                className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md">
                {loading ? "Creating..." : "Create invoice"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {clients.length === 0 && (
        <div className="tc-card p-6 text-sm text-[#5a5a53]">
          You don't have any clients yet. <a href="/app/clients" className="text-[#d95d39] hover:underline font-medium">Add a client first</a> to create invoices.
        </div>
      )}

      <div className="tc-card overflow-hidden overflow-x-auto">
        {invoices.length === 0 ? (
          <div className="p-16 text-center text-sm text-[#5a5a53]" data-testid="invoices-empty">
            No invoices yet. Hit "New invoice" once you have a client.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[720px]" data-testid="invoices-table">
            <thead className="bg-[#f4f1eb] text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">
              <tr>
                <th className="text-left px-6 py-3 font-bold">#</th>
                <th className="text-left px-6 py-3 font-bold">Client</th>
                <th className="text-left px-6 py-3 font-bold">Issue date</th>
                <th className="text-left px-6 py-3 font-bold">Due</th>
                <th className="text-right px-6 py-3 font-bold">Total</th>
                <th className="text-left px-6 py-3 font-bold">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id} className="border-t border-[#e5e5e0] hover:bg-[#fafaf8]">
                  <td className="px-6 py-3 font-mono text-xs">{i.invoice_number}</td>
                  <td className="px-6 py-3">{i.client_snapshot?.name}</td>
                  <td className="px-6 py-3 text-[#5a5a53]">{dateFmt(i.issue_date)}</td>
                  <td className="px-6 py-3 text-[#5a5a53]">{dateFmt(i.due_date)}</td>
                  <td className="px-6 py-3 text-right font-medium">{inr(i.total)}</td>
                  <td className="px-6 py-3"><StatusBadge status={i.status} /></td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <button data-testid={`invoice-pdf-${i.id}`} onClick={() => downloadPdf(i)}
                        className="text-[#5a5a53] hover:text-[#0a0a0a]" title="Download PDF">
                        <FileDown size={16} />
                      </button>
                      {!["paid", "cancelled"].includes(i.status) && (
                        i.payment_link_short_url ? (
                          <button data-testid={`invoice-copy-link-${i.id}`} onClick={() => copyPaymentLink(i.payment_link_short_url)}
                            className="text-[#3d5a8a] hover:text-[#2a446e]" title="Copy payment link">
                            <Copy size={16} />
                          </button>
                        ) : (
                          <button data-testid={`invoice-create-link-${i.id}`} onClick={() => createPaymentLink(i)}
                            className="text-[#d95d39] hover:text-[#c84c35]" title="Create payment link">
                            <Link2 size={16} />
                          </button>
                        )
                      )}
                      {i.status === "draft" && (
                        <button data-testid={`invoice-mark-sent-${i.id}`} onClick={() => sendByEmail(i)}
                          className="text-xs text-[#5a5a53] hover:text-[#0a0a0a] uppercase tracking-wider font-bold">
                          Send
                        </button>
                      )}
                      {!["paid", "cancelled"].includes(i.status) && (
                        <button data-testid={`invoice-mark-paid-${i.id}`} onClick={() => markPaid(i.id)}
                          className="text-[#4a6b53] hover:text-[#3a5441]" title="Mark paid">
                          <Check size={16} />
                        </button>
                      )}
                      {!["paid", "cancelled"].includes(i.status) && (
                        <button data-testid={`invoice-cancel-${i.id}`} onClick={() => cancelInvoice(i.id)}
                          className="text-[#8c6d2a] hover:text-[#6b5221]" title="Cancel invoice">
                          <Ban size={16} />
                        </button>
                      )}
                      <button data-testid={`invoice-delete-${i.id}`} onClick={() => remove(i.id)}
                        className="text-[#5a5a53] hover:text-[#b83a26]" title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
